/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';
import {
  Box,
  Button,
  Grid,
  Icon,
  InputField,
  MuiBox,
  SelectDropdown,
  Typography,
  Tooltip,
  FormDialog
} from '@kis-technologies/kis-ui';
import { NULL_TICKET_TEMPLATE, NullTicketTemplate } from 'constants/forms';
import { useActions } from 'hooks/useActions';
import {
  selectors as ticketTemplatesSelectors,
  actions as ticketTemplatesActions,
  TicketTemplate
} from 'store/ticketTemplates';
import { selectors as authSelectors } from 'store/auth';
import { selectors as ciSelectors, actions as ciActions } from 'store/clientIntegrations';
import { Products as ProductsApiResponse } from 'store/clientIntegrations/types';
import { toDecimal, toPercentage } from 'utils/number';
import { getSortedList, getUniqueName } from 'utils/stringFormat';
import ProductsTable from './components/ProductsTable';
import ProductOverlay from './components/ProductOverlay';
import PriceTypeOverlay from './components/PriceTypeOverlay';
import { KioskTemplateOverlay } from 'views/KiosksView/components/KioskFormOverlay/KioskTemplateOverlay';
import { useStyles } from './styled';
import { InventoryViewProps, ProductsPageData, Product, PriceType, ProductPermissions } from './types';

const InventoryView = ({ setHasUnsavedChanges }: InventoryViewProps) => {
  const admin = useSelector(authSelectors.selectAdmin);
  const clientIntegrations = useSelector(ciSelectors.selectClientIntegrations);
  const paymentSettings = useSelector(ciSelectors.selectPaymentSettings);
  const productsData = useSelector(ciSelectors.selectProducts);
  const actionError = useSelector(ciSelectors.selectError);
  const templates = useSelector(ticketTemplatesSelectors.selectTicketTemplates);

  const [getClientIntegrations, getPaymentSettings, getProducts, syncProducts, resetError] = useActions([
    ciActions.getClientIntegrations,
    ciActions.getPaymentSettings,
    ciActions.getProducts,
    ciActions.syncProducts,
    ciActions.resetError
  ]);
  const [getTicketTemplates] = useActions([ticketTemplatesActions.getTicketTemplates]);

  const role = admin?.role;
  const isAdmin = role === 'ClientAdmin';
  const isGlobalAdmin = role === 'GlobalAdmin';
  const canEditAdminAndGA = isAdmin || isGlobalAdmin;
  const canEditGA = isGlobalAdmin;
  const classes = useStyles();

  const [templateOverlayOpen, setTemplateOverlayOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [environmentId, setEnvironmentId] = useState<string | null>(null);
  const [pageData, setPageData] = useState<ProductsPageData>({
    orderTemplateId: null,
    customFieldLabels: {},
    products: []
  });
  const [productOverlayOpen, setProductOverlayOpen] = useState(false);
  const [priceTypeOverlayOpen, setPriceTypeOverlayOpen] = useState(false);
  const [productOverlayMode, setProductOverlayMode] = useState<'create' | 'edit'>('create');
  const [priceTypeOverlayMode] = useState<'create' | 'edit'>('create');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedPriceType] = useState<PriceType | null>(null);
  const [resetData, setResetData] = useState<ProductsPageData | null>(null);
  const [isDisabled, setIsDisabled] = useState(true);
  const [deleteOverlayOpen, setDeleteOverlayOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);

  const sortedTemplates = getSortedList(templates, 'type', 'name');
  const kisTechnologiesIntegrations = clientIntegrations?.filter((ci) => ci.integration?.code === 'kistechnologies');
  const sortedEnvironments = kisTechnologiesIntegrations?.sort((a, b) => a.name.localeCompare(b.name));
  const orderTemplates = sortedTemplates.filter((t) => t.type === 'receipt');
  const productTemplates = sortedTemplates.filter((t) => t.type === 'receipt' || t.type === 'sale');
  const orderTemplateOptions: (TicketTemplate | NullTicketTemplate)[] = [NULL_TICKET_TEMPLATE, ...orderTemplates];

  useEffect(() => {
    getClientIntegrations();
    getTicketTemplates();
  }, []);

  useEffect(() => {
    if (!environmentId && sortedEnvironments?.length) {
      setEnvironmentId(sortedEnvironments[0].id);
    }
  }, [sortedEnvironments]);

  useEffect(() => {
    if (environmentId) {
      getProducts(environmentId);
      getPaymentSettings(environmentId);
    }
  }, [environmentId]);

  useEffect(() => {
    if (productsData && environmentId) {
      const mapped = mapApiToPageData(productsData);
      setPageData(mapped);
      setResetData(mapped);
    }
  }, [productsData, environmentId]);

  useEffect(() => {
    if (actionError) {
      setErrorMessage('Uh oh! Something went wrong. Please try again.');
      resetError();
    }
  }, [actionError]);

  useEffect(() => {
    if (!resetData) return;

    const isEqual = JSON.stringify(pageData) === JSON.stringify(resetData);
    setIsDisabled(isEqual);
    setHasUnsavedChanges(!isEqual);
  }, [pageData, resetData]);

  const refreshTemplates = () => {
    getTicketTemplates();
  };

  const isSaveDisabled = isDisabled || !environmentId || pageData.products.length === 0;

  const permissions = {
    product: {
      create: canEditAdminAndGA,
      edit: canEditAdminAndGA,
      copy: canEditAdminAndGA,
      delete: canEditAdminAndGA,
      toggle: canEditAdminAndGA,
      save: canEditAdminAndGA,
      editFee: canEditGA
    },
    priceType: {
      create: canEditAdminAndGA,
      edit: canEditAdminAndGA,
      delete: canEditAdminAndGA,
      toggle: canEditAdminAndGA
    },
    template: {
      manage: canEditAdminAndGA
    },
    page: {
      reset: canEditAdminAndGA,
      save: canEditAdminAndGA
    }
  };

  const productPermissions: ProductPermissions = {
    canEditProduct: permissions.product.edit,
    canSaveProduct: permissions.product.save,
    canCreatePriceType: permissions.priceType.create,
    canEditPriceType: permissions.priceType.edit,
    canDeletePriceType: permissions.priceType.delete,
    canTogglePriceType: permissions.priceType.toggle,
    canEditKisFee: permissions.product.editFee,
    canManageTemplates: permissions.template.manage
  };

  const mapApiToPageData = (data: ProductsApiResponse): ProductsPageData => {
    return {
      orderTemplateId: data.orderTicketTemplateId,
      customFieldLabels: data.customFieldLabels ?? {},
      products: data.products.map((p: any) => ({
        id: p.id,
        name: p.name,
        ticketTemplateId: p.ticketTemplateId ?? null,
        kisFeeAmount: p.fees?.[0]?.amount ?? null,
        kisFeePercent: toPercentage(p.fees?.[0]?.percentage),
        enabled: p.enabled,
        configuration: {
          souvenirEventIds: p.configuration.souvenirEventIds ?? [],
          customFields: p.configuration.customFields ?? {}
        },
        priceTypes: p.priceTypes.map((pt: any) => ({
          id: pt.id,
          name: pt.name,
          price: pt.price,
          ticketTemplateId: pt.ticketTemplateId ?? null,
          enabled: pt.enabled
        }))
      }))
    };
  };

  const isGuid = (id?: string) => {
    return id && id.length === 36;
  };

  const buildProductsPayload = (data: ProductsPageData) => {
    return {
      orderTicketTemplateId: data.orderTemplateId,
      customFieldLabels: data.customFieldLabels,
      products: data.products.map((p: Product) => ({
        ...(isGuid(p.id) ? { id: p.id } : {}),
        name: p.name,
        ticketTemplateId: p.ticketTemplateId,
        enabled: p.enabled,
        configuration: p.configuration,
        fees: [
          {
            amount: p.kisFeeAmount ?? null,
            percentage: toDecimal(p.kisFeePercent)
          }
        ],
        priceTypes: p.priceTypes.map((pt: PriceType) => ({
          ...(isGuid(pt.id) ? { id: pt.id } : {}),
          name: pt.name,
          price: pt.price,
          ticketTemplateId: pt.ticketTemplateId,
          enabled: pt.enabled
        }))
      }))
    };
  };

  const handleSave = () => {
    if (!environmentId) return;

    const payload = buildProductsPayload(pageData);

    syncProducts({
      id: environmentId,
      data: payload
    });
  };

  // Save product
  const handleSaveProduct = (product: Product) => {
    setPageData((prev) => {
      let updatedProducts;

      if (productOverlayMode === 'create') {
        updatedProducts = [...prev.products, { ...product }];
      } else {
        updatedProducts = prev.products.map((p) => (p.id === product.id ? product : p));
      }

      return { ...prev, products: updatedProducts };
    });

    setProductOverlayOpen(false);
  };

  const handleCopyProduct = (product: Product) => {
    const timestamp = Date.now().toString();

    const copiedProduct: Product = {
      ...product,
      id: timestamp,
      name: getUniqueName(product.name, pageData.products),
      configuration: cloneDeep(product.configuration),
      priceTypes: product.priceTypes.map((pt, index) => ({
        ...pt,
        id: `${timestamp}-${index}`
      }))
    };

    setPageData((prev) => ({ ...prev, products: [...prev.products, copiedProduct] }));
    setProductOverlayMode('edit');
    setSelectedProduct(copiedProduct);
    setProductOverlayOpen(true);
  };

  const handleDeleteProduct = () => {
    if (!productToDelete) return;

    setPageData((prev) => ({
      ...prev,
      products: prev.products.filter((p) => p.id !== productToDelete.id)
    }));

    setDeleteOverlayOpen(false);
    setProductToDelete(null);
  };

  const handleReset = () => {
    if (resetData) {
      setPageData(resetData);
    }
  };

  return (
    <MuiBox display="flex" flexDirection="column" height="100%">
      {/* Error Overlay */}
      {errorMessage && (
        <FormDialog
          cancelButtonAction={() => setErrorMessage(null)}
          confirmButtonAction={() => setErrorMessage(null)}
          confirmButtonText="Ok"
          open={!!errorMessage}
        >
          <Typography fontSize={24} maxWidth="524px">
            {errorMessage}
          </Typography>
        </FormDialog>
      )}
      {/* Header */}
      <Grid className={classes.headerBlock}>
        <Box flexOverride="none">
          <Typography className={classes.heading}>Inventory</Typography>
        </Box>
        <MuiBox display="flex" alignItems="end" gap="10px">
          <Typography sx={{ marginBottom: '10px', fontSize: '16px', fontWeight: 'bold' }}>Environment</Typography>
          <SelectDropdown
            items={sortedEnvironments || []}
            itemToString={(item) => item?.name || ''}
            selectedItem={sortedEnvironments?.find((e) => e.id === environmentId) || null}
            onChange={(item) => {
              setEnvironmentId(item?.id || null);
            }}
            width="262px"
            radius="round"
            sx={{
              boxShadow: 'none',
              height: '45px',
              '& .MuiSelect-select': {
                color: '#000'
              }
            }}
          />
        </MuiBox>
      </Grid>
      {/* Page Container */}
      <MuiBox padding="0 40px 40px 40px" className={classes.formInnerContainer}>
        <Box raised flexOverride="1 0 0" padding="24px" sx={{ borderRadius: '4px', overflowY: 'auto' }}>
          {/* Order Template Dropdown */}
          <MuiBox className={classes.formBox}>
            <Typography
              className={classes.formLabel}
              sx={{ display: 'flex', justifyContent: 'space-between', width: '262px' }}
            >
              Order Template{' '}
              {canEditAdminAndGA && (
                <Tooltip title={'Manage Templates'} placement="top">
                  <Icon
                    name="Settings"
                    sx={{ cursor: 'pointer', mr: '10px' }}
                    onClick={() => {
                      setTemplateOverlayOpen(true);
                    }}
                  />
                </Tooltip>
              )}
            </Typography>
            <SelectDropdown
              items={orderTemplateOptions}
              itemToString={(item: TicketTemplate | NullTicketTemplate) =>
                `${item.name}${item.type ? ` (${item.type})` : ''}`
              }
              selectedItem={orderTemplateOptions.find((t) => t.id === pageData.orderTemplateId) || NULL_TICKET_TEMPLATE}
              onChange={(item: TicketTemplate | NullTicketTemplate) => {
                setPageData((prev) => ({
                  ...prev,
                  orderTemplateId: item?.id ?? null
                }));
              }}
              width="262px"
              radius="round"
              sx={{
                boxShadow: 'none',
                height: '45px',
                '& .MuiSelect-select': {
                  color: '#000'
                }
              }}
              disabled={!canEditAdminAndGA}
            />
          </MuiBox>

          {/* Custom Field Labels */}
          <MuiBox className={classes.formBox}>
            <Typography className={classes.formLabel}>Custom Field Labels</Typography>
            <MuiBox display="flex" flexWrap="wrap" gap="16px">
              {(['custom1', 'custom2', 'custom3', 'custom4', 'custom5'] as const).map((slot, index) => (
                <MuiBox key={slot} sx={{ width: '262px' }}>
                  <InputField
                    label={`Custom Field ${index + 1}`}
                    size="small"
                    value={pageData.customFieldLabels?.[slot] ?? ''}
                    onChange={(e) =>
                      setPageData((prev) => ({
                        ...prev,
                        customFieldLabels: { ...prev.customFieldLabels, [slot]: e.target.value }
                      }))
                    }
                    sx={{ width: '262px', height: '45px' }}
                    disabled={!canEditAdminAndGA}
                  />
                </MuiBox>
              ))}
            </MuiBox>
          </MuiBox>

          {/* Products Table */}
          <ProductsTable
            products={pageData.products}
            onCreate={() => {
              setProductOverlayMode('create');
              setSelectedProduct(null);
              setProductOverlayOpen(true);
            }}
            onEdit={(product: Product) => {
              setProductOverlayMode('edit');
              setSelectedProduct(product);
              setProductOverlayOpen(true);
            }}
            onCopy={handleCopyProduct}
            onDelete={(product: Product) => {
              setProductToDelete(product);
              setDeleteOverlayOpen(true);
            }}
            onToggle={(product: Product) => {
              setPageData((prev) => ({
                ...prev,
                products: prev.products.map((p) => (p.id === product.id ? { ...p, enabled: !p.enabled } : p))
              }));
            }}
            templates={productTemplates}
            permissions={permissions.product}
          />
        </Box>

        {/* Reset / Save Buttons (UI only for now) */}
        <Box className={classes.buttonGroup}>
          <Button
            customSize="small"
            width={144}
            onClick={handleReset}
            disabled={isDisabled || !permissions.page.reset || !canEditAdminAndGA}
          >
            Reset
          </Button>
          <Button
            customSize="small"
            width={144}
            onClick={handleSave}
            disabled={isSaveDisabled || !permissions.page.save || !canEditAdminAndGA}
          >
            Save
          </Button>
        </Box>

        {/* Product Overlay */}
        {productOverlayOpen && (
          <ProductOverlay
            mode={productOverlayMode}
            initialData={selectedProduct}
            onClose={() => setProductOverlayOpen(false)}
            onSave={handleSaveProduct}
            templates={productTemplates}
            onTemplatesUpdated={refreshTemplates}
            permissions={productPermissions}
            defaultKisProductFee={paymentSettings?.defaultKisProductFee}
            customFieldLabels={pageData.customFieldLabels}
          />
        )}

        {/* Price Type Overlay */}
        {priceTypeOverlayOpen && (
          <PriceTypeOverlay
            mode={priceTypeOverlayMode}
            initialData={selectedPriceType}
            onClose={() => setPriceTypeOverlayOpen(false)}
            onSave={(priceType) => {
              setPageData((prev) => ({
                ...prev,
                products: prev.products.map((p) => {
                  if (p.id === selectedProduct?.id) {
                    return {
                      ...p,
                      priceTypes: [...p.priceTypes, priceType]
                    };
                  }
                  return p;
                })
              }));

              setPriceTypeOverlayOpen(false);
            }}
            templates={productTemplates}
            onTemplatesUpdated={refreshTemplates}
            permissions={productPermissions}
          />
        )}
      </MuiBox>
      {templateOverlayOpen && (
        <KioskTemplateOverlay
          canEdit={permissions.template.manage}
          onClose={() => {
            setTemplateOverlayOpen(false);
            getTicketTemplates(); // refresh templates after closing
          }}
        />
      )}
      {deleteOverlayOpen && (
        <FormDialog
          open={deleteOverlayOpen}
          confirmButtonAction={handleDeleteProduct}
          confirmButtonText="Delete"
          cancelButtonAction={() => {
            setDeleteOverlayOpen(false);
            setProductToDelete(null);
          }}
          cancelButtonText="Cancel"
        >
          <MuiBox>
            <Typography fontSize={24}>Are you sure you want to delete this product?</Typography>
          </MuiBox>
        </FormDialog>
      )}
    </MuiBox>
  );
};

export default InventoryView;
